import React, { useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { z } from 'zod';

import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { ETemplateStudioLayoutOptions } from 'components/TemplatesStudio/enums';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';
import { Separator } from 'components/ui/separator';

import { templateStudioSchema } from '../../../validation-schema';
import { ThemesFormItem } from '../Components/ThemesFormItem';

export const TemplateStudioRollingOffersForm: React.FC = () => {
  const { control, watch } =
    useFormContext<z.infer<typeof templateStudioSchema>>();
  const { supportedThemesForRollingOffers } = useTemplateStudioState();

  const desktopLayoutOptions = useMemo(
    () =>
      Object.values(ETemplateStudioLayoutOptions).map((value) => ({
        value,
        label: value
      })),
    []
  );

  if (!supportedThemesForRollingOffers) return null;

  return (
    <React.Fragment>
      <Separator className="bg-base-border w-[288px] ml-[-2px]" />

      <FormField
        control={control}
        name="rollingOffers.desktopLayout"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-black whitespace-nowrap">
                  Desktop layout
                </span>
                <div className="w-[120px]">
                  <InputSelectDropdown
                    options={desktopLayoutOptions}
                    defaultSelected={desktopLayoutOptions.find(
                      (layout) =>
                        layout.value === watch('rollingOffers.desktopLayout')
                    )}
                    onOptionChange={field.onChange}
                    placeholder="Select product display"
                    isHorizontal={true}
                    buttonClassName={'h-[30px]'}
                    iconClassName="text-xs text-base-mutedForeground"
                    data-testid="TemplatesStudio-RollingOffers-desktopLayout-label"
                    buttonDataTestId="TemplatesStudio-RollingOffers-desktopLayout-button"
                    listDataTestId="TemplatesStudio-RollingOffers-desktopLayout-list"
                  />
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="rollingOffers.themes"
        render={({ field }) => (
          <ThemesFormItem
            onChange={field.onChange}
            value={field.value || []}
            supportedThemes={supportedThemesForRollingOffers}
            themesSelectDataTestId="TemplatesStudio-RollingOffers-themes"
          />
        )}
      />
    </React.Fragment>
  );
};
